import React, { useEffect, useState } from 'react';
import { useQuery, gql, NetworkStatus } from '@apollo/client';
import { useSearchParams } from 'react-router-dom';

// Pestaña "Historial": cada pregunta hecha a la API de preproduccion y lo que respondio.
// Solo lectura. De a 10 preguntas por pagina, de la mas nueva a la mas vieja.
const GET_HISTORIAL = gql`
  query GetHistorialPreguntas($pagina: Int, $porPagina: Int, $entorno: String) {
    getHistorialPreguntas(pagina: $pagina, porPagina: $porPagina, entorno: $entorno) {
      base total pagina paginas porPagina error errorMensaje
      items {
        id chatId chatTitulo chatArchivado fecha pregunta evaluadorEmail evaluadorNombre
        perfilNombre perfilEmail esEquipo respuesta respuestaEstado respuestaFecha duracionSeg tieneInforme
      }
    }
  }
`;

const GET_DETALLE = gql`
  query GetDetallePreguntaIA($id: ID!, $entorno: String) {
    getDetallePreguntaIA(id: $id, entorno: $entorno) {
      id chatId chatTitulo fecha base
      evaluadoNombre evaluadoFechaNacimiento evaluadoEmail evaluadoOrigen evaluadoCreado evaluadoActualizado evaluadoArchivado
      evaluadoAvisos { tipo mensaje }
      esEquipo integrantes { id nombre email fechaNacimiento }
      evaluadorEmail evaluadorNombre integrador tenant modeloInferencia
      llamadas { orden metodo ruta descripcion cuerpo fecha }
      consumo { requestId resultado modelo tokensEntrada tokensSalida tokensCache duracionMs fecha }
      consumoNota
    }
  }
`;

const POR_PAGINA = 10;
const TZ = 'America/Argentina/Buenos_Aires';
const LARGO_CORTO = 700;
const ESTADOS = {
  ok: ['tag--green', 'Completa'],
  partial: ['tag--yellow', 'Parcial'],
  error: ['tag--red', 'Falló'],
  blocked: ['tag--yellow', 'Rechazada por políticas'],
};
const fecha = (iso) => (iso ? new Date(iso).toLocaleString('es-AR', {
  timeZone: TZ, day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
}) : '');
const duracion = (s) => (s === null || s === undefined ? '' : s < 60 ? `${s} s` : `${Math.floor(s / 60)} min ${s % 60} s`);

// Fecha de nacimiento como la guarda la API (AAAA-MM-DD) o como viene de una ficha (DDMMAAAA).
const nacimiento = (v) => {
  const s = String(v || '').trim();
  let m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s);
  if (m) return `${m[3]}/${m[2]}/${m[1]}`;
  m = /^(\d{2})(\d{2})(\d{4})$/.exec(s);
  return m ? `${m[1]}/${m[2]}/${m[3]}` : s || null;
};
const RESULTADOS = { ok: 'Completa', partial: 'Parcial', error: 'Falló', blocked: 'Rechazada por políticas', aborted: 'Cortada por el cliente', timeout: 'Se pasó del tiempo' };
const miles = (n) => new Intl.NumberFormat('es-AR').format(n || 0);

// Modal "Detalle": que datos del evaluado tenia la API y por que endpoints paso la pregunta.
function DetallePregunta({ id, entorno, onCerrar }) {
  const { data, loading, error } = useQuery(GET_DETALLE, { variables: { id, entorno }, fetchPolicy: 'network-only' });
  const d = data?.getDetallePreguntaIA;
  useEffect(() => {
    const tecla = (e) => { if (e.key === 'Escape') onCerrar(); };
    window.addEventListener('keydown', tecla);
    return () => window.removeEventListener('keydown', tecla);
  }, [onCerrar]);
  const Fila = ({ k, v }) => <div className="detail-row"><span className="detail-label">{k}</span><span className="detail-value">{v || '—'}</span></div>;
  return (
    <div className="modal-overlay" onClick={onCerrar}>
      <div className="modal-card hp-detalle" role="dialog" aria-modal="true" aria-label="Detalle de la pregunta" onClick={(e) => e.stopPropagation()}>
        <div className="modal-head">
          <div>
            <h2 style={{ fontSize: '1.15rem', fontWeight: 700, margin: 0 }}>Detalle de la pregunta</h2>
            <p style={{ color: '#9ca3af', fontSize: '0.85rem', margin: '2px 0 0' }}>{d ? `${fecha(d.fecha)}${d.chatTitulo ? ` · ${d.chatTitulo}` : ''}` : ''}</p>
          </div>
          <button className="modal-close" aria-label="Cerrar" onClick={onCerrar}>×</button>
        </div>
        {loading && !d && <div className="spinner" />}
        {error && <div className="cs-aviso cs-aviso--error">{error.graphQLErrors?.[0]?.message || error.message}</div>}
        {d && (
          <>
            <div className="detail-section-title">{d.esEquipo ? 'Equipo evaluado' : 'Persona evaluada'}</div>
            <Fila k={d.esEquipo ? 'Nombre del equipo' : 'Nombre completo'} v={d.evaluadoNombre} />
            {!d.esEquipo && <Fila k="Fecha de nacimiento" v={nacimiento(d.evaluadoFechaNacimiento)} />}
            {!d.esEquipo && <Fila k="Email" v={d.evaluadoEmail} />}
            <Fila k="Origen del perfil" v={d.evaluadoOrigen} />
            {d.evaluadoCreado && <Fila k="Perfil creado" v={`${fecha(d.evaluadoCreado)}${d.evaluadoActualizado && d.evaluadoActualizado !== d.evaluadoCreado ? ` · modificado ${fecha(d.evaluadoActualizado)}` : ''}${d.evaluadoArchivado ? ' · archivado' : ''}`} />}
            {d.esEquipo && d.integrantes.length > 0 && (
              <div className="hp-integrantes">
                {d.integrantes.map((x) => <div key={x.id} className="hp-integrante"><b>{x.nombre || 'Sin nombre'}</b><span>{[x.email, nacimiento(x.fechaNacimiento)].filter(Boolean).join(' · ') || '—'}</span></div>)}
              </div>
            )}
            {d.evaluadoAvisos.length > 0 && (
              <ul className="hp-avisos">
                {d.evaluadoAvisos.map((a) => <li key={a.mensaje} className={`hp-aviso hp-aviso--${a.tipo}`}>{a.mensaje}</li>)}
              </ul>
            )}
            {d.evaluadoAvisos.some((a) => a.tipo === 'error') && <p className="hp-nota hp-nota--bloqueo">Este perfil se cargó antes de la regla del 25/10/2026. Desde entonces la API no lo deja usar (mensajes, tópicos, informes ni equipos) y responde <code>422</code> hasta que el integrador lo corrija con <code>PATCH /v1/profiles/{'{profileId}'}</code>. Sus conversaciones anteriores se conservan.</p>}
            <p className="hp-nota">Son los datos actuales del perfil en la API: si el integrador los cambió después, se ven los nuevos.</p>

            <div className="detail-section-title">Endpoints involucrados</div>
            <p className="hp-nota">Base: <code>{d.base}</code>. Los cuerpos se reconstruyen con lo que guardó la API.</p>
            <ol className="hp-llamadas">
              {d.llamadas.map((l) => (
                <li key={l.orden}>
                  <div className="hp-llamada-cabeza">
                    <span className={`hp-metodo hp-metodo--${l.metodo === 'POST' ? 'post' : 'otro'}`}>{l.metodo}</span>
                    <code>{l.ruta}</code>
                    {l.fecha && <span className="hp-nota-chica">{fecha(l.fecha)}</span>}
                  </div>
                  <div className="hp-nota-chica">{l.descripcion}</div>
                  {l.cuerpo && <pre className="hp-cuerpo">{l.cuerpo}</pre>}
                </li>
              ))}
            </ol>

            <div className="detail-section-title">Consumo de la pregunta</div>
            {d.consumo ? (
              <>
                <Fila k="Resultado" v={RESULTADOS[d.consumo.resultado] || d.consumo.resultado} />
                <Fila k="Modelo" v={d.consumo.modelo} />
                <Fila k="Tokens" v={`${miles(d.consumo.tokensEntrada)} de entrada · ${miles(d.consumo.tokensSalida)} de salida${d.consumo.tokensCache ? ` · ${miles(d.consumo.tokensCache)} en caché` : ''}`} />
                <Fila k="Duración" v={d.consumo.duracionMs !== null ? `${(d.consumo.duracionMs / 1000).toFixed(1)} s` : null} />
                <Fila k="requestId" v={d.consumo.requestId && <code>{d.consumo.requestId}</code>} />
              </>
            ) : <p className="hp-nota">{d.consumoNota}</p>}

            <div className="detail-section-title">Quién preguntó</div>
            <Fila k="Evaluador" v={[d.evaluadorNombre, d.evaluadorEmail].filter(Boolean).join(' · ')} />
            <Fila k="Integrador" v={d.integrador} />
            <Fila k="Cuenta (tenant)" v={d.tenant} />
            <Fila k="Modelo de inferencia" v={d.modeloInferencia} />
          </>
        )}
      </div>
    </div>
  );
}

// Markdown basico a elementos de React (sin HTML crudo): titulos, listas, tablas, negrita, italica y codigo.
function enLinea(texto, k) {
  const partes = String(texto).split(/(\*\*[^*]+\*\*|`[^`]+`|\b_[^_\n]+_\b)/g);
  return partes.map((p, i) => {
    if (/^\*\*[^*]+\*\*$/.test(p)) return <strong key={`${k}-${i}`}>{p.slice(2, -2)}</strong>;
    if (/^`[^`]+`$/.test(p)) return <code key={`${k}-${i}`}>{p.slice(1, -1)}</code>;
    if (/^_[^_\n]+_$/.test(p)) return <em key={`${k}-${i}`}>{p.slice(1, -1)}</em>;
    return <React.Fragment key={`${k}-${i}`}>{p}</React.Fragment>;
  });
}
function TextoMarkdown({ texto }) {
  const lineas = String(texto || '').replace(/\r/g, '').split('\n');
  const bloques = [];
  let lista = null;
  let tabla = null;
  const cerrarLista = () => { if (lista) { bloques.push(lista); lista = null; } };
  const cerrarTabla = () => { if (tabla) { bloques.push(tabla); tabla = null; } };
  const celdas = (l) => l.trim().replace(/^\|/, '').replace(/\|$/, '').split('|').map((c) => c.trim());
  lineas.forEach((l, i) => {
    if (/^\s*\|.*\|\s*$/.test(l)) {
      cerrarLista();
      if (!tabla) tabla = { tipo: 'tabla', k: i, filas: [], encabezado: null };
      if (/^\s*\|?[\s:|-]+\|?\s*$/.test(l) && l.includes('-')) {
        if (!tabla.encabezado && tabla.filas.length === 1) tabla.encabezado = tabla.filas.shift();
        return;
      }
      tabla.filas.push(celdas(l).map((c, j) => enLinea(c, `${i}-${j}`)));
      return;
    }
    cerrarTabla();
    const vineta = /^\s*(?:[-*•])\s+(.*)$/.exec(l);
    const numero = /^\s*\d+[.)]\s+(.*)$/.exec(l);
    const titulo = /^\s*#{1,6}\s+(.*)$/.exec(l);
    if (vineta || numero) {
      const tipo = vineta ? 'ul' : 'ol';
      if (!lista || lista.tipo !== tipo) { cerrarLista(); lista = { tipo, items: [], k: i }; }
      lista.items.push(enLinea((vineta || numero)[1], i));
      return;
    }
    cerrarLista();
    if (titulo) bloques.push({ tipo: 'h', k: i, c: enLinea(titulo[1], i) });
    else if (/^\s*(-{3,}|\*{3,})\s*$/.test(l)) bloques.push({ tipo: 'hr', k: i });
    else if (l.trim()) bloques.push({ tipo: 'p', k: i, c: enLinea(l, i) });
  });
  cerrarLista();
  cerrarTabla();
  return (
    <div className="hp-md">
      {bloques.map((b) => {
        if (b.tipo === 'h') return <p key={b.k} className="hp-md-titulo">{b.c}</p>;
        if (b.tipo === 'hr') return <hr key={b.k} />;
        if (b.tipo === 'p') return <p key={b.k}>{b.c}</p>;
        if (b.tipo === 'tabla') {
          return (
            <div key={b.k} className="hp-md-tabla">
              <table>
                {b.encabezado && <thead><tr>{b.encabezado.map((c, j) => <th key={j}>{c}</th>)}</tr></thead>}
                <tbody>{b.filas.map((f, j) => <tr key={j}>{f.map((c, x) => <td key={x}>{c}</td>)}</tr>)}</tbody>
              </table>
            </div>
          );
        }
        const Lista = b.tipo;
        return <Lista key={b.k}>{b.items.map((it, j) => <li key={j}>{it}</li>)}</Lista>;
      })}
    </div>
  );
}

function Respuesta({ item }) {
  const [abierta, setAbierta] = useState(false);
  if (item.respuesta === null) return <p className="hp-vacio">Sin respuesta registrada.</p>;
  if (!item.respuesta.trim()) return <p className="hp-vacio">La respuesta quedó vacía.</p>;
  const larga = item.respuesta.length > LARGO_CORTO;
  return (
    <>
      <div className={`hp-respuesta-texto ${larga && !abierta ? 'hp-cortada' : ''}`}>
        <TextoMarkdown texto={item.respuesta} />
      </div>
      {larga && (
        <button className="btn-subtle hp-ver-mas" onClick={() => setAbierta(!abierta)}>
          {abierta ? 'Ver menos' : 'Ver respuesta completa'}
        </button>
      )}
    </>
  );
}

export function Paginador({ pagina, paginas, ir }) {
  if (paginas <= 1) return null;
  const desde = Math.max(1, Math.min(pagina - 2, paginas - 4));
  const numeros = Array.from({ length: Math.min(5, paginas) }, (_, i) => desde + i);
  return (
    <nav className="hp-paginador" aria-label="Páginas del historial">
      <button className="btn-outline" disabled={pagina <= 1} onClick={() => ir(pagina - 1)}>‹ Anterior</button>
      {numeros[0] > 1 && <><button className="btn-outline" onClick={() => ir(1)}>1</button>{numeros[0] > 2 && <span>…</span>}</>}
      {numeros.map((n) => (
        <button key={n} className={n === pagina ? 'btn-accent' : 'btn-outline'} aria-current={n === pagina ? 'page' : undefined} onClick={() => ir(n)}>{n}</button>
      ))}
      {numeros[numeros.length - 1] < paginas && <>{numeros[numeros.length - 1] < paginas - 1 && <span>…</span>}<button className="btn-outline" onClick={() => ir(paginas)}>{paginas}</button></>}
      <button className="btn-outline" disabled={pagina >= paginas} onClick={() => ir(pagina + 1)}>Siguiente ›</button>
    </nav>
  );
}

export default function HistorialPreguntas({ entorno = 'pre' }) {
  const [params, setParams] = useSearchParams();
  const [detalle, setDetalle] = useState(null);
  const pedida = Math.max(1, parseInt(params.get('p') || '1', 10) || 1);
  const { data, loading, error, refetch, networkStatus } = useQuery(GET_HISTORIAL, {
    variables: { pagina: pedida, porPagina: POR_PAGINA, entorno },
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
  });
  const r = data?.getHistorialPreguntas;
  const ir = (n) => { setParams({ ...(params.get('amb') ? { amb: params.get('amb') } : {}), tab: 'historial', ...(n > 1 ? { p: String(n) } : {}) }); window.scrollTo({ top: 0, behavior: 'smooth' }); };
  const cargando = loading || networkStatus === NetworkStatus.refetch;
  const desde = r && r.total ? (r.pagina - 1) * r.porPagina + 1 : 0;
  const hasta = r ? desde + r.items.length - 1 : 0;

  return (
    <>
      <div className="panel-card cia-barra-herramientas">
        <span className="hp-resumen">
          {r && !r.error ? (r.total ? <>Preguntas <b>{desde}</b> a <b>{hasta}</b> de <b>{r.total}</b>, de la más nueva a la más vieja</> : 'Todavía no hay preguntas') : ' '}
        </span>
        <button className="btn-accent" disabled={cargando} onClick={() => refetch()}>{cargando ? 'Actualizando…' : 'Actualizar'}</button>
      </div>

      {cargando && !r && <div className="spinner" />}
      {error && <div className="cs-aviso cs-aviso--error">{error.message}</div>}
      {r?.error && <div className="cs-aviso cs-aviso--error">{r.errorMensaje}</div>}

      {r && !r.error && r.total === 0 && (
        <div className="panel-card cia-seccion"><div className="empty-state">Nawaiam todavía no hizo ninguna pregunta en preproducción.</div></div>
      )}

      {r && !r.error && r.items.length > 0 && (
        <>
          <Paginador pagina={r.pagina} paginas={r.paginas} ir={ir} />
          <div className="hp-lista">
            {r.items.map((it) => {
              const [clase, texto] = ESTADOS[it.respuestaEstado] || ['tag--gray', 'Sin respuesta'];
              return (
                <article key={it.id} className="panel-card hp-item">
                  <header className="hp-meta">
                    <span className="hp-fecha">{fecha(it.fecha)}</span>
                    <span className="hp-quien">{it.evaluadorEmail || 'Evaluador desconocido'}</span>
                    {(it.perfilNombre || it.perfilEmail) && (
                      <span className="tag tag--blue">{it.esEquipo ? 'Equipo: ' : ''}{it.perfilNombre || it.perfilEmail}</span>
                    )}
                    <span className={`tag ${clase}`}>{texto}</span>
                    {it.tieneInforme && <span className="tag tag--purple">Con informe</span>}
                    {it.chatArchivado && <span className="tag tag--gray">Conversación archivada</span>}
                    <button type="button" className="hp-detalle-boton" onClick={() => setDetalle(it.id)}>Detalle</button>
                  </header>
                  {it.chatTitulo && <p className="hp-chat">Conversación: {it.chatTitulo}</p>}
                  <div className="hp-bloque hp-pregunta">
                    <span className="hp-rotulo">Pregunta</span>
                    <p>{it.pregunta}</p>
                  </div>
                  <div className="hp-bloque hp-respuesta">
                    <span className="hp-rotulo">
                      Respuesta de la API{it.duracionSeg !== null && it.respuesta ? ` · en ${duracion(it.duracionSeg)}` : ''}
                    </span>
                    <Respuesta item={it} />
                  </div>
                </article>
              );
            })}
          </div>
          <Paginador pagina={r.pagina} paginas={r.paginas} ir={ir} />
        </>
      )}
      {detalle && <DetallePregunta id={detalle} entorno={entorno} onCerrar={() => setDetalle(null)} />}
    </>
  );
}
